import type { ChartDataset } from 'chart.js';
import { BLOCKER_GROUPS } from '../blockerGroups';
import type { BlockerSummary, BlockerTrendPoint } from '../blockerGroups';
import { num } from '../utils';
import { ChartBox } from './ChartBox';

interface BlockerTrendPanelProps {
  points: BlockerTrendPoint[];
  summary: BlockerSummary;
  periodLabel: (period: string) => string;
}

const pct = (part: number, total: number) => total ? Math.round((part / total) * 1000) / 10 : 0;

export function BlockerTrendPanel({ points, summary, periodLabel }: BlockerTrendPanelProps) {
  const blockerTotal = summary.known + summary.unknown;
  const unknownPct = pct(summary.unknown, blockerTotal);
  const hasKnownPoints = points.some(point => point.known > 0);

  return (
    <section className="card space blocker-trend-card">
      <div className="section-title blocker-trend-title">
        <div>
          <h2>Xu hướng tỷ lệ không chốt theo nhóm nguyên nhân</h2>
          <span>Mẫu số từng kỳ: các lượt có lý do đã xác định · tổng 4 nhóm = 100%</span>
        </div>
        <span>{num(summary.known)} đã xác định · {num(summary.unknown)} chưa xác định ({unknownPct.toLocaleString('vi-VN')}%)</span>
      </div>

      <div className="blocker-rate-grid">
        {BLOCKER_GROUPS.map(group => (
          <div className="blocker-rate" key={group.key}>
            <span className="blocker-rate-dot" style={{ background: group.color }} />
            <div>
              <span>{group.label}</span>
              <b>{pct(summary.counts[group.key], summary.known).toLocaleString('vi-VN')}%</b>
              <small>{num(summary.counts[group.key])} lượt · {group.description}</small>
            </div>
          </div>
        ))}
      </div>

      {!hasKnownPoints ? (
        <div className="empty blocker-trend-empty">Chưa đủ lý do đã xác định để dựng trend trong khoảng đang chọn.</div>
      ) : (
        <div className="chart tall">
          <ChartBox
            type="bar"
            labels={points.map(point => periodLabel(point.period))}
            datasets={BLOCKER_GROUPS.map(group => ({
              label: group.label,
              data: points.map(point => pct(point.counts[group.key], point.known)),
              type: 'bar',
              stack: 'reasons',
              borderColor: group.color,
              backgroundColor: `${group.color}d9`,
              borderWidth: 0,
              maxBarThickness: 42,
              categoryPercentage: 0.88,
              barPercentage: 0.96,
            } as unknown as ChartDataset))}
            options={{
              scales: {
                x: { stacked: true, grid: { display: false } },
                y: {
                  stacked: true,
                  beginAtZero: true,
                  max: 100,
                  ticks: { callback: value => `${value}%` },
                  title: { display: true, text: '% lý do đã xác định' },
                },
              },
              plugins: {
                legend: { position: 'bottom', labels: { boxWidth: 10 } },
                datalabels: { display: false },
                tooltip: {
                  callbacks: {
                    label: context => `${context.dataset.label}: ${Number(context.parsed.y || 0).toLocaleString('vi-VN')}%`,
                  },
                },
              },
            }}
          />
        </div>
      )}
      <p className="blocker-trend-note">“Chưa xác định” được tách khỏi mẫu số để không che mất xu hướng kinh doanh; bar subcode chi tiết vẫn giữ bên dưới để drill-down.</p>
    </section>
  );
}
